import { useState } from "react";
import { Button, Toaster, TreeMultiSelect } from "@jana-technology/platform-ui";
import type { FilterItemProps } from "@jana-technology/platform-ui";

import { ExportSelect } from "components/selects/ExportSelect";
import { useExportContext } from "contexts/export/context";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";

const getLeafValues = (items: Array<FilterItemProps>): Array<string> =>
  items.flatMap((item) =>
    item.children?.length ? getLeafValues(item.children) : [item.value],
  );

const getFieldIds = (values: Array<string>): Array<number> =>
  values
    .map(Number)
    .filter((fieldId) => Number.isInteger(fieldId) && fieldId >= 0);

export const ExportPage = () => {
  const {
    reportTypes,
    selectedReportType,
    setSelectedReportType,
    fetchExportData,
  } = useExportContext();
  const { hierarchyIdCheckboxes, assetSelectionQueryResult } =
    useGlobalFiltersContext();
  const [selectedFieldValues, setSelectedFieldValues] = useState<
    Array<string> | undefined
  >();
  const [isExporting, setIsExporting] = useState(false);

  const allFieldValues = getLeafValues(hierarchyIdCheckboxes);
  const resolvedFieldValues = selectedFieldValues ?? allFieldValues;
  const selectedFieldIds = getFieldIds(resolvedFieldValues);
  const submitDisabled =
    selectedReportType.length === 0 || selectedFieldIds.length === 0;

  const handleSubmit = async () => {
    if (submitDisabled || isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      await fetchExportData(selectedReportType, selectedFieldIds);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <form
        id="export-form"
        name="export-form"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="flex items-end gap-4">
          <ExportSelect
            label="Report Type*:"
            items={reportTypes}
            value={selectedReportType}
            onValueChange={setSelectedReportType}
          />
          <div className="flex flex-col">
            <label className="mb-2 font-medium" htmlFor="export-fields">
              Select Field(s)*:
            </label>
            <TreeMultiSelect
              id="export-fields"
              className="w-72"
              defaultExpandedValues={allFieldValues}
              disabled={assetSelectionQueryResult.loading}
              onValueChange={setSelectedFieldValues}
              options={hierarchyIdCheckboxes}
              placeholder={
                assetSelectionQueryResult.loading
                  ? "Loading..."
                  : "Select field(s)"
              }
              renderValue={({ selectedLabels, selectedValues }) =>
                allFieldValues.length > 0 &&
                selectedValues.length === allFieldValues.length
                  ? "(All fields selected)"
                  : selectedLabels.join(", ") || undefined
              }
              value={resolvedFieldValues}
            />
          </div>
          <Button disabled={submitDisabled} isBusy={isExporting} type="submit">
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </form>
      <Toaster />
    </div>
  );
};

export default ExportPage;
